import { Injectable } from "@angular/core";
import { CHANNEL_OPS_INT_ACTION } from "../IOpsIntActionService";
import { OpsIntPromiseActionService, OpsIntActionResponse } from "./OpsIntPromiseActionService";
import { CubiqWorker } from "../../worker/CubiqWorker";
import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { NotificationService } from "../../lookup/notification/impl/NotificationService";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { SysFeatures, SystemConfigPromiseService } from "shared/auth-services";

const DEFAULT_ASYNC_OPS_CHUNKS = 50.0;

const serial = (funcs) =>
  funcs.reduce(
    (promise, func) => promise.then((result) => func().then(Array.prototype.concat.bind(result))),
    Promise.resolve([]),
  );

@Injectable()
export class OpsIntPrimaryActionService extends OpsIntPromiseActionService {
  protected broker: ServiceMessageBroker;
  protected asynchOpsChunks = DEFAULT_ASYNC_OPS_CHUNKS;

  constructor(
    private worker: CubiqWorker,
    protected factory: ServiceMessageBrokerFactory,
    private notifService: NotificationService,
    private sysConfigService: SystemConfigPromiseService,
  ) {
    super();

    this.sysConfigService.getSystemConfig(SysFeatures.asynchOpsChunks).then((values) => {
      if (values != undefined) {
        let asyncOpsChunks = Number(values);
        if (asyncOpsChunks > 0) {
          this.asynchOpsChunks = asyncOpsChunks;
        }
      }
    });

    this.broker = this.factory.createMessageBroker(CHANNEL_OPS_INT_ACTION, true, true);
    this.broker.registerMethod(
      "cancelOrder",
      [PRIMITIVE],
      this.cancelOrder.bind(this),
      CubiqTS.ENCODABLE(CubiqTS.ServiceResponseImpl),
    );
    this.broker.registerMethod(
      "clear",
      [PRIMITIVE, PRIMITIVE, PRIMITIVE, PRIMITIVE],
      this.cancelOrder.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "reprice",
      [PRIMITIVE],
      this.reprice.bind(this),
      CubiqTS.ENCODABLE(CubiqTS.ServiceResponseImpl),
    );
    this.broker.registerMethod(
      "resend",
      [PRIMITIVE],
      this.resend.bind(this),
      CubiqTS.ENCODABLE(CubiqTS.ServiceResponseImpl),
    );
  }

  getClearingData(id: string): CubiqTS.OpsExcClearingDataImpl {
    let clearingData = new CubiqTS.OpsExcClearingDataImpl();
    clearingData.opsExceptionId = CubiqTS.Long.fromValue(id);
    clearingData.clearingData = "";
    return clearingData;
  }

  cancelOrder(opsIntId: string[]): Promise<OpsIntActionResponse> {
    let requests = [];
    let batchCount = Math.ceil(opsIntId.length / this.asynchOpsChunks);
    for (let batchIndex = 0; batchIndex < batchCount; batchIndex++) {
      let request = new CubiqTS.OpsExceptionActionRequestImpl();
      request.cancelOrders = opsIntId.map((id) => this.getClearingData(id));
      requests.push(request);
    }
    return this.postServiceRequestChunks(requests);
  }

  clear(
    opsIntId: string[],
    comment: string,
    externalDealId: string | null,
    options: object = {},
  ): Promise<OpsIntActionResponse> {
    let requests = [];
    let batchCount = Math.ceil(opsIntId.length / this.asynchOpsChunks);
    for (let batchIndex = 0; batchIndex < batchCount; batchIndex++) {
      let request = new CubiqTS.OpsExceptionActionRequestImpl();
      request.clearing = opsIntId.map((id) => {
        let clearingData = this.getClearingData(id);
        if (comment != null && typeof comment === "string") {
          clearingData.clearingData = comment;
        }

        if (externalDealId != null && typeof externalDealId === "string") {
          // TODO not sure if this is correct yet.
          clearingData.externalDealId = externalDealId;
        }
        return clearingData;
      });
      requests.push(request);
    }
    return this.postServiceRequestChunks(requests);
  }

  reprice(opsIntId: string[]): Promise<OpsIntActionResponse> {
    let requests = [];
    let batchCount = Math.ceil(opsIntId.length / this.asynchOpsChunks);
    for (let batchIndex = 0; batchIndex < batchCount; batchIndex++) {
      let request = new CubiqTS.OpsExceptionActionRequestImpl();
      request.repriceOrders = opsIntId.map((id) => this.getClearingData(id));
      requests.push(request);
    }
    return this.postServiceRequestChunks(requests);
  }

  resend(opsIntId: string[]): Promise<OpsIntActionResponse> {
    let requests = [];
    let batchCount = Math.ceil(opsIntId.length / this.asynchOpsChunks);
    for (let batchIndex = 0; batchIndex < batchCount; batchIndex++) {
      let request = new CubiqTS.OpsExceptionActionRequestImpl();
      request.resendDeals = opsIntId.map((id) => this.getClearingData(id));
      requests.push(request);
    }
    return this.postServiceRequestChunks(requests);
  }

  private postServiceRequestChunks(
    chunks: CubiqTS.OpsExceptionActionRequestImpl[],
  ): Promise<OpsIntActionResponse> {
    return new Promise<any>((resolve, reject) => {
      let responses = [];
      while (chunks.length) {
        responses.push(
          function (request) {
            let responsePromise = this.worker.postServiceRequest(request);
            responsePromise.then((serviceRespose: null | CubiqTS.ConcreteEncodable) => {
              // expected case is a null response so check for error in response
              if (serviceRespose != null) {
                logger.out.warn(
                  `${this.constructor.name} unexpected service response %O`,
                  serviceRespose,
                );
              }
            });
            return responsePromise;
          }.bind(this, chunks.shift()),
        );
      }

      return serial(responses).then((values) => resolve(values));
    });
  }
}
